import { EditIcon } from '../components/icons';
import { currencyFormatter, PercentageFormatter } from '../pages/QuotePage/content/QuoteGrids/utils';
import { isEmptyArray, normalizeCamelCase } from '../ui/utils';
import { apiHelpers } from './apiHelpers';
import { calculateCoverageLimits, getDefaultCoverages } from './calculateCoverageLimits';
import { formatAddress } from './getFormattedAddress';

const formatRange = (range, coveredValue) => {
  const cleanedRange = range.replace(/^\(|\)$/g, '');
  const [min, max] = cleanedRange.split(',').map((value) => value.trim());
  const formatToMillions = (num) => (num ? `$${(parseInt(num, 10) / 1000000).toFixed(0)}M` : '');
  const minFormatted = formatToMillions(min) || '$0M';
  const maxFormatted = formatToMillions(max);

  if (min && max) {
    return `${minFormatted} to ${maxFormatted}`;
  } else if (min) {
    return `${minFormatted} and above`;
  } else if (max) {
    if (parseInt(max, 10) === 5000000 && coveredValue < 5000000) {
      return 'Under $5M';
    }
    return `Up to ${maxFormatted}`;
  } else {
    return '$0M and above';
  }
};

const getCoverageColDef = ({
  isEditable,
  setValue,
  quotes,
  adjusting,
  setModalOpen,
  setMode,
  setLocationData,
  floodPayoutLimit,
  earthquakePayoutLimit,
}) => {
  const getColValues = (params, coverageType, key) => {
    const { data } = params;
    const coverage = data.coverages && data.coverages.length && data.coverages.find((each) => each.coverageType === coverageType);

    if (coverage) {
      return coverage[key];
    }

    return '';
  };

  const getTotalPremium = ({ data }) => {
    const { facProgramExpenses = 0, primaryPremium = 0, autoFacPremium = 0, additionalFacPremium = 0 } = data.reinsurance || {};
    return +facProgramExpenses + +primaryPremium + +autoFacPremium + +additionalFacPremium;
  };

  const getCSR = (params) => {
    const { data } = params;
    if (data.riskZones && data.riskZones[0]?.id) return `CSR - ${data.riskZones[0]?.id}`;
  };

  const getLocationIndex = (riskId) => quotes.locations.findIndex((location) => location.riskId === riskId);

  const changedRows = new Set();

  const isAverageInsuredChanged = (params) => {
    return changedRows.has(params.node.id);
  };

  const handleOnInsuredValueChange = (params) => {
    const riskId = params.data.riskId;
    const locationIndex = getLocationIndex(riskId);
    params.data.coveredValue = params.newValue;
    changedRows.add(params.node.id);
    if (!params.data.reinsurance) params.data.reinsurance = {};

    params.data.reinsurance.maxTotalInsuredValue = params.newValue * 1.25;
    params.data.maxAvgTIV = (params.data.reinsurance.maxTotalInsuredValue / params.data.coveredValue) * 100;
    const avgTotalInsuredValue = params.newValue;
    if (isEmptyArray(params.data.coverages)) {
      params.data.coverages = getDefaultCoverages(quotes, params.data);
    }
    params.data.coverages.forEach((coverage, coverageIndex) => {
      coverage.payoutLimit = calculateCoverageLimits(coverage.coverageType, avgTotalInsuredValue);
      if (coverage.coverageType === 'hail' && params.data.requirements?.hailAggDeductiblePerc) {
        coverage.aggregate = (params.newValue * params.data.requirements.hailAggDeductiblePerc) / 100;
      }
      if (adjusting) {
        setValue(`quote.locations.${locationIndex}.coverages.${coverageIndex}.payoutLimit`, coverage.payoutLimit);
        if (coverage.coverageType === 'hail') {
          setValue(`quote.locations.${locationIndex}.coverages.${coverageIndex}.aggregate`, coverage.aggregate);
        }
      }
    });
    if (adjusting) {
      setValue(`quote.locations.${locationIndex}.coveredValue`, params.newValue);
      setValue(`quote.locations.${locationIndex}.reinsurance.maxTotalInsuredValue`, params.newValue * 1.25);
    }
    return true;
  };

  const handleMaxInsuredValueChange = (params) => {
    const riskId = params.data.riskId;
    const locationIndex = getLocationIndex(riskId);

    if (!params.data.reinsurance) {
      params.data.reinsurance = {};
    }
    params.data.reinsurance.maxTotalInsuredValue = +params.newValue;
    setValue(`quote.locations.${locationIndex}.reinsurance.maxTotalInsuredValue`, +params.newValue);
    params.data.maxAvgTIV = (params.newValue / params.data.coveredValue) * 100;
    return true;
  };

  const handleMaxAvgTIVChange = (params) => {
    const riskId = params.data.riskId;
    const locationIndex = getLocationIndex(riskId);

    if (isNaN(params.newValue)) return false;

    const newPercentage = params.newValue;
    params.data.maxAvgTIV = newPercentage;

    if (!params.data.reinsurance) params.data.reinsurance = {};

    const maxTotalInsuredValue = parseInt(((newPercentage / 100) * params.data.coveredValue).toFixed(2));
    params.data.reinsurance.maxTotalInsuredValue = maxTotalInsuredValue;
    setValue(`quote.locations.${locationIndex}.reinsurance.maxTotalInsuredValue`, maxTotalInsuredValue);

    return true;
  };

  const onHailAggDedPercentChanged = (params) => {
    const riskId = params.data.riskId;
    const locationIndex = getLocationIndex(riskId);

    const averageInsuredValue = params.data.coveredValue;
    const coverageId = params.column.userProvidedColDef.coverageId;
    const coverageIndex = params.data.coverages.findIndex((coverage) => coverage.coverageId === coverageId);
    const hailAggDedPercent = +params.newValue;
    const hailAgg = parseInt(((averageInsuredValue * hailAggDedPercent) / 100).toFixed(2));
    params.data.coverages[coverageIndex].aggregate = hailAgg;
    params.node.setDataValue('aggregate', hailAgg);
    setValue(`quote.locations.${locationIndex}.coverages.${coverageIndex}.aggregate`, hailAgg);
  };

  const setGridValues = (params, key, sublimit) => {
    const { data, newValue } = params;
    const riskId = params.data.riskId;
    const locationIndex = getLocationIndex(riskId);
    let coverageId = '';
    let editedField = '';
    if (key === 'flood') {
      coverageId = 21;
      editedField = 'payoutLimit';
    } else {
      coverageId = params.column.userProvidedColDef.coverageId;
      editedField = params.colDef.field;
    }
    const coverageIndex = data.coverages.findIndex((coverage) => coverage.coverageId === coverageId);
    let formattedValue = 0;
    if (key === 'flood') {
      const payoutLimit = data.coverages[coverageIndex]['payoutLimit'];
      if (floodPayoutLimit.current === null) {
        floodPayoutLimit.current = payoutLimit;
      }
      if (sublimit) formattedValue = Math.min(1000000, Number(payoutLimit));
      else formattedValue = Number(floodPayoutLimit.current);
    } else formattedValue = isNaN(Number(newValue)) ? 0 : Number(newValue);
    if (params.colDef.headerName === 'Hail Agg Deductible') {
      const hailAggDedPercent = +((formattedValue / params.data.coveredValue) * 100).toFixed(2);
      params.data.requirements.hailAggDeductiblePerc = hailAggDedPercent;
      setValue(`quote.locations.${locationIndex}.requirements.hailAggDeductiblePerc`, hailAggDedPercent);
    }
    adjusting && setValue(`quote.locations.${locationIndex}.coverages.${coverageIndex}.${editedField}`, formattedValue);
    data.coverages[coverageIndex][editedField] = formattedValue;
    return true;
  };

  const cellRenderer = (params) => {
    const handleIconClick = (event) => {
      event.stopPropagation();
      setLocationData(params.data);
      setMode('EDIT');
      setModalOpen(true);
    };
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span onClick={handleIconClick} style={{ display: isEditable() ? 'flex' : 'none' }} className="edit-icon">
          <EditIcon />
        </span>
        <span>{params.value}</span>
      </div>
    );
  };

  const getTierValue = (params) => {
    const key = params.colDef.field;
    const riskZones = params.data?.riskZones;
    if (Array.isArray(riskZones) && riskZones.length > 0) {
      return riskZones[0][key] !== undefined ? riskZones[0][key] : false;
    }
    return false;
  };

  const getAdditionalColumns = (coverageType) => {
    switch (coverageType) {
      case 'comprehensive':
        return [{ field: 'details.crimeZone', headerName: 'Crime Index', isRequired: false, dataType: 'string' }];
      case 'flood':
        return [
          {
            headerName: 'Flood Zone',
            field: 'details.femaZone',
            cellClassRules: {
              'red-background': (params) => params.data?.details?.isFloodZone,
            },
            editable: isEditable,
            valueSetter: (params) => {
              if (!params.data.details) {
                params.data.details = {};
              }
              params.data.details.femaZone = params.newValue;
              params.data.details.femaZoneOverride = params.newValue;
              const riskId = params.data.riskId;
              const locationIndex = getLocationIndex(riskId);
              setValue(`quote.locations.${locationIndex}.details.femaZoneOverride`, params.newValue);
              return true;
            },
            minWidth: 150,
          },
          {
            headerName: 'Map',
            field: 'mapLink',
            minWidth: 160,
            valueGetter: (params) => {
              const lat = params.data?.lat;
              const lon = params.data?.lon;

              if (lat && lon) {
                const latFixed = parseFloat(lat).toFixed(7);
                const lonFixed = parseFloat(lon).toFixed(7);
                const url = `https://msc.fema.gov/portal/search?AddressQuery=${lonFixed}%2C%20${latFixed}#searchresultsanchor`;
                return url;
              } else {
                return '';
              }
            },
            cellRenderer: function (url) {
              return (
                <a href={url.value} target="_blank" rel="noopener noreferrer">
                  {url.value}
                </a>
              );
            },
          },
          {
            headerName: 'Distance from Coast',
            field: 'coastDistance',
            minWidth: 160,
            valueGetter: (params) => {
              const coastDistance = params.data?.details?.coastDistance;

              if (typeof coastDistance === 'string') {
                return coastDistance;
              }

              if (!isNaN(coastDistance)) {
                return coastDistance.toFixed(2) + ' ' + 'miles';
              }
              return '';
            },
          },
          {
            headerName: 'SWAP',
            field: 'swap',
            cellClassRules: {
              'ag-cell-editable': (params) => isEditable(params),
            },
            valueGetter: (params) => {
              if (params.data?.requirements?.hasOwnProperty('swapNeeded')) {
                return params.data.requirements.swapNeeded;
              } else return false;
            },
            valueSetter: (params) => {
              if (!params.data.requirements) {
                params.data.requirements = {};
              }
              params.data.requirements.swapNeeded = params.newValue;
              const riskId = params.data.riskId;
              const locationIndex = getLocationIndex(riskId);
              setValue(`quote.locations.${locationIndex}.requirements.swapNeeded`, params.newValue);
              return true;
            },
            editable: isEditable,
            cellRenderer: 'agCheckboxCellRenderer',
            cellEditor: 'agCheckboxCellEditor',
            exportValueFormatter: (params) => (params.value ? 'Yes' : 'No'),
            minWidth: 100,
          },
          {
            headerName: '',
            resizable: false,
            valueGetter: (params) => {
              if (!params.data.requirements) {
                params.data.requirements = {};
              }
              return !params.data.requirements.hasOwnProperty('floodSublimit') || params.data.requirements.floodSublimit === null;
            },
            valueSetter: (params) => {
              if (!params.data.requirements) {
                params.data.requirements = {};
              }

              if (params.newValue) {
                if (params.data.requirements.hasOwnProperty('floodSublimit') && params.data.requirements.floodSublimit !== null) {
                  params.data.requirements._originalFloodSublimit = params.data.requirements.floodSublimit;
                }

                const riskId = params.data.riskId;
                const locationIndex = getLocationIndex(riskId);
                params.data.requirements.floodSublimit = null;
                params.data.requirements._floodSublimitChecked = true;
                setValue(`quote.locations.${locationIndex}.requirements.floodSublimit`, null);
              } else {
                const originalValue =
                  params.data.requirements._originalFloodSublimit !== undefined ? params.data.requirements._originalFloodSublimit : null;

                params.data.requirements.floodSublimit = originalValue;
                const riskId = params.data.riskId;
                const locationIndex = getLocationIndex(riskId);
                params.data.requirements._floodSublimitChecked = false;
                setValue(`quote.locations.${locationIndex}.requirements.floodSublimit`, originalValue);
              }
              params.data.requirements._floodSublimitChanged = true;
              return true;
            },
            cellClassRules: {
              'ag-cell-editable': (params) => isEditable(params),
            },
            editable: isEditable,
            cellRenderer: 'agCheckboxCellRenderer',
            cellEditor: 'agCheckboxCellEditor',
            exportValueFormatter: (params) => (params.value ? 'Yes' : 'No'),
            minWidth: 50,
          },
          {
            headerName: 'Flood Sublimit',
            field: 'floodSublimit',
            valueGetter: (params) => {
              return params.data?.requirements?.floodSublimit;
            },
            valueSetter: (params) => {
              if (!params.data.requirements) {
                params.data.requirements = {};
              }

              const riskId = params.data.riskId;
              const locationIndex = getLocationIndex(riskId);

              params.data.requirements.floodSublimit = params.newValue;
              setValue(`quote.locations.${locationIndex}.requirements.floodSublimit`, Number(params.newValue));
              return true;
            },
            cellClassRules: {
              'ag-cell-editable': (params) => {
                if (!params.data.requirements?.hasOwnProperty('_floodSublimitChecked')) {
                  return (
                    isEditable(params) &&
                    params.data?.requirements?.floodSublimit !== null &&
                    params.data?.requirements?.hasOwnProperty('floodSublimit')
                  );
                }
                return isEditable(params) && !params.data.requirements._floodSublimitChecked;
              },
              'gray-background': (params) => {
                if (!params.data.requirements?.hasOwnProperty('_floodSublimitChecked')) {
                  return !params.data?.requirements?.hasOwnProperty('floodSublimit') || params.data?.requirements?.floodSublimit === null;
                }
                return params.data.requirements._floodSublimitChecked;
              },
            },
            editable: (params) => {
              if (!params.data.requirements?.hasOwnProperty('_floodSublimitChecked')) {
                return (
                  isEditable(params) &&
                  params.data?.requirements?.floodSublimit !== null &&
                  params.data?.requirements?.hasOwnProperty('floodSublimit')
                );
              }
              return isEditable(params) && !params.data.requirements._floodSublimitChecked;
            },
            valueFormatter: (params) => {
              if (!params.data.requirements?.hasOwnProperty('_floodSublimitChecked')) {
                return !params.data.requirements?.hasOwnProperty('floodSublimit') || params.data.requirements?.floodSublimit === null
                  ? 'Full Sublimit'
                  : currencyFormatter({ field: { value: params.value || 0 } });
              }

              if (params.data.requirements._floodSublimitChecked) {
                return 'Full Sublimit';
              } else {
                return params.data.requirements.floodSublimit === null ? '' : currencyFormatter({ field: { value: params.value || 0 } });
              }
            },
            minWidth: 160,
          },
        ];
      case 'earthquake':
        return [
          {
            headerName: 'Earthquake Zone',
            field: 'earthquakeZone',
            valueGetter: (params) => (params.data.riskZones && params.data.riskZones[0]?.skywardEarthquake) || false,
            cellRenderer: 'agCheckboxCellRenderer',
            cellEditor: 'agCheckboxCellEditor',
            exportValueFormatter: (params) => (params.value ? 'Yes' : 'No'),

            minWidth: 200,
          },
          {
            headerName: '',
            resizable: false,
            valueGetter: (params) => {
              if (!params.data.requirements) {
                params.data.requirements = {};
              }
              return !params.data.requirements.hasOwnProperty('earthquakeSublimit') || params.data.requirements.earthquakeSublimit === null;
            },
            valueSetter: (params) => {
              if (!params.data.requirements) {
                params.data.requirements = {};
              }

              if (params.newValue) {
                if (params.data.requirements.hasOwnProperty('earthquakeSublimit') && params.data.requirements.earthquakeSublimit !== null) {
                  params.data.requirements._originalEarthquakeSublimit = params.data.requirements.earthquakeSublimit;
                }

                const riskId = params.data.riskId;
                const locationIndex = getLocationIndex(riskId);
                params.data.requirements.earthquakeSublimit = null;
                params.data.requirements._earthquakeSublimitChecked = true;
                setValue(`quote.locations.${locationIndex}.requirements.earthquakeSublimit`, null);
              } else {
                const originalValue =
                  params.data.requirements._originalEarthquakeSublimit !== undefined ? params.data.requirements._originalEarthquakeSublimit : null;

                params.data.requirements.earthquakeSublimit = originalValue;
                const riskId = params.data.riskId;
                const locationIndex = getLocationIndex(riskId);
                params.data.requirements._earthquakeSublimitChecked = false;
                setValue(`quote.locations.${locationIndex}.requirements.earthquakeSublimit`, originalValue);
              }
              params.data.requirements._earthquakeSublimitChanged = true;
              return true;
            },
            cellClassRules: {
              'ag-cell-editable': (params) => isEditable(params),
            },
            editable: isEditable,
            cellRenderer: 'agCheckboxCellRenderer',
            cellEditor: 'agCheckboxCellEditor',
            exportValueFormatter: (params) => (params.value ? 'Yes' : 'No'),
            minWidth: 50,
          },
          {
            headerName: 'Earthquake Sublimit',
            field: 'earthquakeSublimit',
            valueGetter: (params) => {
              return params.data?.requirements?.earthquakeSublimit;
            },
            valueSetter: (params) => {
              if (!params.data.requirements) {
                params.data.requirements = {};
              }

              const riskId = params.data.riskId;
              const locationIndex = getLocationIndex(riskId);

              params.data.requirements.earthquakeSublimit = params.newValue;
              setValue(`quote.locations.${locationIndex}.requirements.earthquakeSublimit`, Number(params.newValue));
              return true;
            },
            cellClassRules: {
              'ag-cell-editable': (params) => {
                if (!params.data.requirements?.hasOwnProperty('_earthquakeSublimitChecked')) {
                  return (
                    isEditable(params) &&
                    params.data?.requirements?.earthquakeSublimit !== null &&
                    params.data?.requirements?.hasOwnProperty('earthquakeSublimit')
                  );
                }
                return isEditable(params) && !params.data.requirements._earthquakeSublimitChecked;
              },
              'gray-background': (params) => {
                if (!params.data.requirements?.hasOwnProperty('_earthquakeSublimitChecked')) {
                  return !params.data?.requirements?.hasOwnProperty('earthquakeSublimit') || params.data?.requirements?.earthquakeSublimit === null;
                }
                return params.data.requirements._earthquakeSublimitChecked;
              },
            },
            editable: (params) => {
              if (!params.data.requirements?.hasOwnProperty('_earthquakeSublimitChecked')) {
                return (
                  isEditable(params) &&
                  params.data?.requirements?.earthquakeSublimit !== null &&
                  params.data?.requirements?.hasOwnProperty('earthquakeSublimit')
                );
              }
              return isEditable(params) && !params.data.requirements._earthquakeSublimitChecked;
            },
            valueFormatter: (params) => {
              if (!params.data.requirements?.hasOwnProperty('_earthquakeSublimitChecked')) {
                return !params.data.requirements?.hasOwnProperty('earthquakeSublimit') || params.data.requirements?.earthquakeSublimit === null
                  ? 'Full Sublimit'
                  : currencyFormatter({ field: { value: params.value || 0 } });
              }

              if (params.data.requirements._earthquakeSublimitChecked) {
                return 'Full Sublimit';
              } else {
                return params.data.requirements.earthquakeSublimit === null ? '' : currencyFormatter({ field: { value: params.value || 0 } });
              }
            },
          },
        ];
      case 'hail':
        return [
          { headerName: 'CSR', field: 'csr', valueGetter: (params) => getCSR(params), minWidth: 100 },
          {
            headerName: 'Dot?',
            field: 'dot',
            cellClassRules: {
              'ag-cell-editable': (params) => isEditable(params),
            },
            editable: isEditable,
            cellRenderer: 'agCheckboxCellRenderer',
            cellEditor: 'agCheckboxCellEditor',

            exportValueFormatter: (params) => (params.value ? 'Yes' : 'No'),
            valueGetter: (params) => {
              if (params.data?.requirements?.hasOwnProperty('dotNeeded')) return params.data.requirements.dotNeeded;
              else return false;
            },
            minWidth: 100,
            valueSetter: (params) => {
              if (!params.data.reinsurance) {
                params.data.reinsurance = {};
              }
              params.data.requirements.dotNeeded = params.newValue;
              const riskId = params.data.riskId;
              const locationIndex = getLocationIndex(riskId);
              setValue(`quote.locations.${locationIndex}.requirements.dotNeeded`, params.newValue);
              return true;
            },
          },
          {
            headerName: 'Hail Agg Deductible %',
            field: 'hailAggDedPercent',
            dataType: 'number',
            onCellValueChanged: onHailAggDedPercentChanged,
            cellClassRules: {
              'ag-cell-editable': (params) => isEditable(params),
            },
            editable: isEditable,
            valueSetter: (params) => {
              if (!params.data.reinsurance) {
                params.data.reinsurance = {};
              }
              params.data.requirements.hailAggDeductiblePerc = Number(params.newValue);
              const riskId = params.data.riskId;
              const locationIndex = getLocationIndex(riskId);
              setValue(`quote.locations.${locationIndex}.requirements.hailAggDeductiblePerc`, +params.newValue);
              return true;
            },
            valueGetter: (params) => params.data?.requirements?.hailAggDeductiblePerc || 0,
            valueFormatter: (field) => (field.value ? PercentageFormatter(field) : 'N/A'),
            coverageId: 9,
          },
        ];
      default:
        return [];
    }
  };

  const maxAutoFacLayers = () =>
    quotes.locations.reduce((max, loc) => {
      const n = loc?.reinsurance?.autoFacLayers?.length || 0;
      return n > max ? n : max;
    }, 0);

  const autoFacLayerColumns = () => {
    const layerCount = maxAutoFacLayers();
    const cols = [];
    for (let i = 0; i < layerCount; i += 1) {
      const layerNo = i + 1;
      cols.push({
        headerName: `Layer ${layerNo} Rate ($ per $M)`,
        isRequired: false,
        dataType: 'number',
        minWidth: 150,
        valueGetter: (params) => params.data?.reinsurance?.autoFacLayers?.[i]?.rate ?? 0,
        valueFormatter: (field) => (field.value ? currencyFormatter({ field, withDecimals: true }) : 'N/A'),
        cellClassRules: { 'gray-background': isAverageInsuredChanged },
      });
      cols.push({
        headerName: `Layer ${layerNo} Premium`,
        isRequired: false,
        dataType: 'number',
        minWidth: 150,
        valueGetter: (params) => params.data?.reinsurance?.autoFacLayers?.[i]?.premium ?? 0,
        valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
        cellClassRules: { 'gray-background': isAverageInsuredChanged },
      });
    }
    return cols;
  };

  // Present only when the engine actually rated Fac XOL somewhere on the quote (2026-tower
  // card, TIV above the trigger) — pre-2026 quotes keep today's grid. Grossed value, matching
  // the workbook's Location Details column; engine-computed on serve, so never editable.
  const facXolColumn = () => {
    const hasFacXol = quotes.locations.some((loc) => loc?.reinsurance?.facXolGrossed != null);
    if (!hasFacXol) return [];
    return [
      {
        headerName: 'Fac XOL',
        colId: 'facXolGrossed',
        isRequired: false,
        dataType: 'number',
        minWidth: 150,
        valueGetter: (params) => params.data?.reinsurance?.facXolGrossed ?? 0,
        valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
        cellClassRules: { 'gray-background': isAverageInsuredChanged },
      },
    ];
  };

  const generateCoverageColumns = () => {
    const createCoverageColumns = (coverage) => {
      const { coverageType, coverageId } = coverage;
      const additionalColumns = getAdditionalColumns(coverageType);
      const shortName = apiHelpers.getShortCoveragesName(coverageType);
      let commonColumns = [];
      if (coverageType === 'falsePretense' || coverageType === 'terrorism') {
        commonColumns.push({
          headerName: `${shortName} Premium`,
          field: 'netPremium',
          dataType: 'number',
          valueGetter: (params) => getColValues(params, coverageType, 'netPremium'),
          valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
          coverageId,
          cellClassRules: {
            'gray-background': isAverageInsuredChanged,
          },
        });
        commonColumns.push({
          headerName: `${shortName} Per Car Deductible`,
          field: 'deductible',
          dataType: 'number',
          valueGetter: (params) => getColValues(params, coverageType, 'deductible'),
          valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
          editable: isEditable,
          valueSetter: (params) => setGridValues(params),
          cellClassRules: { 'ag-cell-editable': (params) => isEditable(params) },
          coverageId,
        });
      } else {
        commonColumns = [
          {
            headerName: `${shortName} Per Car Deductible`,
            field: 'deductible',
            dataType: 'number',
            valueGetter: (params) => getColValues(params, coverageType, 'deductible'),
            valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
            editable: isEditable,
            valueSetter: (params) => setGridValues(params),
            cellClassRules: { 'ag-cell-editable': (params) => isEditable(params) },
            coverageId,
          },
          {
            headerName: `${shortName} Agg Deductible`,
            field: 'aggregate',
            dataType: 'number',
            valueGetter: (params) => getColValues(params, coverageType, 'aggregate'),
            valueFormatter: (field) => {
              const value = field.value;
              return value === 0 || isNaN(value) ? 'N/A' : currencyFormatter({ field });
            },
            editable: isEditable,
            valueSetter: (params) => setGridValues(params),
            coverageId,
            cellClassRules: { 'ag-cell-editable': (params) => isEditable(params) },
          },
          {
            headerName: `${shortName} Payout Limit`,
            field: 'payoutLimit',
            dataType: 'number',
            valueGetter: (params) => getColValues(params, coverageType, 'payoutLimit'),
            valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
            editable: coverageType === 'flood' ? false : isEditable,
            valueSetter: (params) => setGridValues(params),
            cellClassRules: {
              'ag-cell-editable': (params) => (coverageType === 'flood' ? false : isEditable(params)),
              'gray-background': (params) =>
                (coverageType === 'flood' && params.data?.requirements?._floodSublimitChanged === true) ||
                (coverageType === 'earthquake' && params.data?.requirements?._earthquakeSublimitChanged === true),
            },
            coverageId,
          },
          {
            headerName: `${shortName} Premium`,
            field: 'netPremium',
            dataType: 'number',
            valueGetter: (params) => getColValues(params, coverageType, 'netPremium'),
            valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
            coverageId,
            cellClassRules: {
              'gray-background': isAverageInsuredChanged,
            },
          },
        ];
      }

      return {
        headerName: normalizeCamelCase(coverageType),
        children: [...additionalColumns, ...commonColumns],
      };
    };

    return getDefaultCoverages(quotes).map(createCoverageColumns);
  };

  return [
    {
      headerName: 'Overview',
      children: [
        {
          headerName: 'Location',
          field: 'riskId',
          headerCheckboxSelection: isEditable(),
          checkboxSelection: isEditable(),
          isRequired: false,
          dataType: 'number',
          pinned: true,
          sort: 'asc',
          cellClassRules: {
            'yellow-background': (params) => isEditable(params),
          },
          maxWidth: 110,
        },
        {
          headerName: 'Name',
          field: 'name',
          cellRenderer: cellRenderer,
          isRequired: false,
          dataType: 'string',
          pinned: true,
          cellClassRules: {
            'yellow-background': (params) => isEditable(params),
          },
        },
        {
          headerName: 'Address',
          field: 'address',
          isRequired: false,
          dataType: 'string',
          pinned: true,
          minWidth: 250,
          cellClassRules: {
            'yellow-background': (params) => isEditable(params),
          },
          valueGetter: (params) => {
            const visibleAddress = formatAddress(params.data.visibleAddress) || '';
            const address = formatAddress(params.data.address) || '';
            if (visibleAddress === address) {
              return visibleAddress || address;
            }
            return visibleAddress && address ? `${visibleAddress}, ${address}` : visibleAddress || address;
          },
        },
        {
          headerName: 'County',
          field: 'county',
          isRequired: false,
          dataType: 'string',
          minWidth: 150,
        },
        { headerName: 'State', field: 'regionCode', isRequired: false, dataType: 'string', minWidth: 100 },
        {
          headerName: 'Avg Total Insured Value',
          valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
          isRequired: false,
          dataType: 'number',
          exportValueFormatter: (params) => params.value,
          field: 'coveredValue',
          editable: isEditable,
          cellClassRules: {
            'yellow-background': (params) => isEditable(params),
          },
          minWidth: 200,
          valueSetter: (params) => handleOnInsuredValueChange(params),
        },
        {
          headerName: 'Max Total Insured Value',
          field: 'maxTotalInsuredValue',
          valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
          valueGetter: (params) => {
            const avgValue = params.data.coveredValue;
            return params.data.reinsurance?.maxTotalInsuredValue || avgValue;
          },
          isRequired: false,
          cellClassRules: {
            'ag-cell-editable': (params) => isEditable(params),
          },
          editable: isEditable,
          minWidth: 200,
          dataType: 'number',
          valueSetter: (params) => handleMaxInsuredValueChange(params),
          exportValueFormatter: (params) => params.value,
        },
        {
          headerName: 'Max/Average TIV',
          isRequired: false,
          field: 'maxAvgTIV',
          valueGetter: (params) => {
            const { coveredValue } = params.data;
            const { maxTotalInsuredValue = 0 } = params.data.reinsurance || {};
            const avgValue = coveredValue;
            const maxInsured = maxTotalInsuredValue || coveredValue;
            const maxInsuredPercentage = params.data.maxAvgTIV || (maxInsured / avgValue) * 100;
            return +parseFloat(maxInsuredPercentage).toFixed(2);
          },
          valueFormatter: (field) => (field.value ? PercentageFormatter(field) : 'N/A'),
          cellClassRules: {
            'ag-cell-editable': (params) => isEditable(params),
          },
          valueSetter: (params) => handleMaxAvgTIVChange(params),
          editable: isEditable,
          dataType: 'number',
          minWidth: 200,
        },
        {
          headerName: 'Primary Premium',
          isRequired: false,
          field: 'primaryPremium',
          valueGetter: (params) => {
            const { primaryPremium = 0 } = params.data.reinsurance || {};
            return primaryPremium;
          },
          valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
          valueSetter: (params) => {
            if (!params.data.reinsurance) {
              params.data.reinsurance = {};
            }
            params.data.reinsurance.primaryPremium = params.newValue;
            const riskId = params.data.riskId;
            const locationIndex = getLocationIndex(riskId);
            setValue(`quote.locations.${locationIndex}.reinsurance.primaryPremium`, Number(params.newValue));
            return true;
          },
          dataType: 'number',
          minWidth: 200,
          cellClassRules: {
            'gray-background': isAverageInsuredChanged,
          },
        },
        {
          headerName: 'Total Auto Fac',
          colId: 'totalAutoFac',
          valueGetter: (params) => {
            const layers = params.data?.reinsurance?.autoFacLayers || [];
            return layers.reduce((sum, l) => sum + Number(l.premium || 0), 0);
          },
          valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
          isRequired: false,
          dataType: 'number',
          minWidth: 150,
          cellClassRules: {
            'gray-background': isAverageInsuredChanged,
          },
        },
        {
          headerName: 'Total Premium',
          valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
          valueGetter: (params) => getTotalPremium(params),
          isRequired: false,
          dataType: 'number',
          exportValueFormatter: (params) => params.value,
          minWidth: 200,
          cellClassRules: {
            'gray-background': isAverageInsuredChanged,
          },
        },
        {
          headerName: 'Rate',
          valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
          valueGetter: (params) => (getTotalPremium(params) / params.data.coveredValue) * 100,
          isRequired: false,
          dataType: 'number',
          minWidth: 100,
          cellClassRules: {
            'gray-background': isAverageInsuredChanged,
          },
        },
      ],
    },
    {
      headerName: 'Reinsurance',
      children: [
        {
          headerName: 'CRZ',
          field: 'riskZones.0.convectiveRatingZone',
          isRequired: false,
          valueGetter: (params) => params.data.riskZones && params.data.riskZones[0]?.convectiveRatingZone,
          dataType: 'string',
          minWidth: 100,
        },
        {
          headerName: 'TIV Band',
          field: 'riskZones.0.autoFacBand',
          isRequired: false,
          valueGetter: (params) => params.data?.riskZones?.[0]?.autoFacBand,
          dataType: 'string',
          minWidth: 150,
          valueFormatter: (params) => {
            const band = params.data?.riskZones && params.data.riskZones[0]?.autoFacBand;
            return band && formatRange(band, params.data?.coveredValue);
          },
          cellClassRules: {
            'gray-background': isAverageInsuredChanged,
          },
          exportValueFormatter: (params) => {
            const { node } = params;
            const band =
              node?.data?.riskZones && Array.isArray(node.data.riskZones) && node.data.riskZones.length > 0
                ? node.data.riskZones[0]?.autoFacBand
                : null;
            return band ? formatRange(band, node.data?.coveredValue) : '';
          },
        },

        {
          headerName: 'Tier 1 Wind (Ascot)',
          isRequired: false,
          field: 'ascotExcluded',
          dataType: 'boolean',
          minWidth: 150,
          valueGetter: getTierValue,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          exportValueFormatter: (params) => (params.value ? 'Yes' : 'No'),
        },
        {
          headerName: 'Tier 1 Wind (Arch)',
          isRequired: false,
          field: 'archExcluded',
          dataType: 'boolean',
          minWidth: 150,
          valueGetter: getTierValue,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          exportValueFormatter: (params) => (params.value ? 'Yes' : 'No'),
        },
        {
          headerName: 'Tier 1 Wind (Everest)',
          isRequired: false,
          field: 'everestExcluded',
          dataType: 'boolean',
          minWidth: 150,
          valueGetter: getTierValue,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          exportValueFormatter: (params) => (params.value ? 'Yes' : 'No'),
        },
        { field: 'details.wildfire', headerName: 'Wildfire Score', isRequired: false, dataType: 'string', minWidth: 150 },

        {
          headerName: 'Excluded ?',
          isRequired: false,
          field: 'excluded',
          colId: 'excluded',
          cellClassRules: {
            'ag-cell-editable': (params) => isEditable(params),
          },
          editable: isEditable,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          dataType: 'boolean',
          minWidth: 150,
          valueGetter: (params) => {
            if (params.data?.reinsurance?.hasOwnProperty('excluded')) return params.data.reinsurance.excluded;
            else return false;
          },
          exportValueFormatter: (params) => (params.value ? 'Yes' : 'No'),
          valueSetter: (params) => {
            if (!params.data.reinsurance) {
              params.data.reinsurance = {};
            }
            if (params.data.reinsurance.initialAutoFacRate === undefined) {
              params.data.reinsurance.initialAutoFacRate = params.data.reinsurance.autoFacRate;
            }
            if (params.data.reinsurance.initialAutoFacPremium === undefined) {
              params.data.reinsurance.initialAutoFacPremium = params.data.reinsurance?.autoFacPremium;
            }
            const initialAutoFacRate = params.data.reinsurance.initialAutoFacRate;
            const initialAutoFacPremium = params.data.reinsurance.initialAutoFacPremium;

            params.data.reinsurance.excluded = params.newValue;
            const riskId = params.data.riskId;
            const locationIndex = getLocationIndex(riskId);
            setValue(`quote.locations.${locationIndex}.reinsurance.excluded`, params.newValue);

            if (params.newValue) {
              params.data.reinsurance.autoFacRate = 0;
              params.data.reinsurance.autoFacPremium = 0;
              setValue(`quote.locations.${locationIndex}.reinsurance.autoFacRate`, 0);
              setValue(`quote.locations.${locationIndex}.reinsurance.autoFacPremium`, 0);
            } else {
              params.data.reinsurance.autoFacRate = initialAutoFacRate;
              params.data.reinsurance.autoFacPremium = initialAutoFacPremium;
              setValue(`quote.locations.${locationIndex}.reinsurance.autoFacRate`, initialAutoFacRate);
              setValue(`quote.locations.${locationIndex}.reinsurance.autoFacPremium`, initialAutoFacPremium);
            }

            return true;
          },
        },
        ...autoFacLayerColumns(),
        {
          headerName: 'IR Fac',
          colId: 'additionalFacPremium',
          valueGetter: (params) => {
            const { additionalFacPremium } = params.data.reinsurance || {};
            if (additionalFacPremium != null) {
              return additionalFacPremium;
            }
            return 0;
          },
          valueSetter: (params) => {
            if (!params.data.reinsurance) {
              params.data.reinsurance = {};
            }
            params.data.reinsurance.additionalFacPremium = params.newValue;
            const riskId = params.data.riskId;
            const locationIndex = getLocationIndex(riskId);
            setValue(`quote.locations.${locationIndex}.reinsurance.additionalFacPremium`, Number(params.newValue));
            return true;
          },
          valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
          cellClassRules: {
            'gray-background': isAverageInsuredChanged,
          },
          editable: isEditable,
          isRequired: false,
          dataType: 'number',
          minWidth: 150,
        },
        ...facXolColumn(),
        {
          headerName: 'IR Fac Only',
          isRequired: false,
          colId: 'additionalFacOnly',
          cellClassRules: {
            'ag-cell-editable': (params) => isEditable(params),
          },
          editable: isEditable,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          dataType: 'boolean',
          minWidth: 150,
          valueGetter: (params) => {
            if (params.data?.reinsurance?.hasOwnProperty('additionalFacOnly')) return params.data.reinsurance.additionalFacOnly;
            else return false;
          },
          exportValueFormatter: (params) => (params.value ? 'Yes' : 'No'),
          valueSetter: (params) => {
            if (!params.data.reinsurance) {
              params.data.reinsurance = {};
            }
            params.data.reinsurance.additionalFacOnly = params.newValue;
            const riskId = params.data.riskId;
            const locationIndex = getLocationIndex(riskId);
            setValue(`quote.locations.${locationIndex}.reinsurance.additionalFacOnly`, params.newValue);
            return true;
          },
        },
        {
          headerName: 'IR Fac Already Grossed',
          isRequired: false,
          colId: 'additionalFacAlreadyGrossed',
          cellClassRules: {
            'ag-cell-editable': (params) => isEditable(params),
          },
          editable: isEditable,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          dataType: 'boolean',
          minWidth: 180,
          valueGetter: (params) => {
            // Defaults to checked: IR fac entered from SPAs is typically already grossed.
            if (params.data?.reinsurance?.hasOwnProperty('additionalFacAlreadyGrossed')) return params.data.reinsurance.additionalFacAlreadyGrossed;
            else return true;
          },
          exportValueFormatter: (params) => (params.value ? 'Yes' : 'No'),
          valueSetter: (params) => {
            if (!params.data.reinsurance) {
              params.data.reinsurance = {};
            }
            params.data.reinsurance.additionalFacAlreadyGrossed = params.newValue;
            const riskId = params.data.riskId;
            const locationIndex = getLocationIndex(riskId);
            setValue(`quote.locations.${locationIndex}.reinsurance.additionalFacAlreadyGrossed`, params.newValue);
            return true;
          },
        },
        {
          headerName: 'Fac Program Expenses',
          colId: 'facProgramExpenses',
          valueGetter: (params) => {
            const { facProgramExpenses } = params.data.reinsurance || {};
            if (facProgramExpenses != null) {
              return facProgramExpenses;
            }
            return 0;
          },
          valueSetter: (params) => {
            if (!params.data.reinsurance) {
              params.data.reinsurance = {};
            }
            params.data.reinsurance.facProgramExpenses = params.newValue;
            const riskId = params.data.riskId;
            const locationIndex = getLocationIndex(riskId);
            setValue(`quote.locations.${locationIndex}.reinsurance.facProgramExpenses`, Number(params.newValue));
            return true;
          },
          valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
          cellClassRules: {
            'gray-background': isAverageInsuredChanged,
          },
          isRequired: false,
          dataType: 'number',
          minWidth: 150,
        },
        {
          headerName: 'Fac % GWP',
          valueGetter: (params) => {
            const { autoFacPremium = 0, additionalFacPremium = 0, facProgramExpenses = 0 } = params.data?.reinsurance || {};
            const totalFac = Number(autoFacPremium) + Number(additionalFacPremium) + Number(facProgramExpenses);
            const totalPremium = getTotalPremium(params);
            return (Number(totalPremium) && (Number(totalFac) / Number(totalPremium)) * 100) || 0;
          },
          isRequired: false,
          dataType: 'number',
          minWidth: 150,
          valueFormatter: PercentageFormatter,
        },
      ],
    },
    ...generateCoverageColumns(),
  ];
};
export default getCoverageColDef;
